/**
 * AI跟单 - 下单表单组件（金额输入、滑块、余额、门槛）
 * 用于：加仓页、跟单入口（两处共用，按钮文案通过 submitLabel 区分「加仓」/「立即跟单」）
 * 后续可统一改成图2样式时只改本组件即可。
 */
import React, { useRef, useEffect, useState, useMemo } from "react";
import cx from "classnames";
import { Hooks, Context } from "@xt/xtbase";
import { resolveCouponTenantId } from "@/api/coupon-user";
import { translateVoucherApiMc } from "@/components/Pages/Activity/Voucher/translateVoucherApiMc";
import TradeSlider from "../Slider";
import FiatCurrencyIcon from "@/components/Pages/Convert/FiatCurrencyIcon";
import { formatAicopyAmountRangeLabel } from "../../utils/getInvestmentAmountErrorMsg";
import { isAicopyVoucherOrderOption } from "../../utils/aicopyOrderOptions";
import {
  normalizeAicopyBalanceRaw,
  parseAicopyBalanceNum,
  resolveAicopySliderMaxBalanceStr,
} from "../../utils/aicopyCurrencies";
import {
  fetchAicopyUsableVouchersCached,
  AICOPY_USABLE_VOUCHERS_INVALIDATE_EVENT,
} from "../../utils/fetchAicopyUsableVouchers";
import { isVoucherItemSelectableForFollow, parseVoucherFaceAmount, isVoucherAmountInFollowRange } from "../../utils/voucherFollowRange";
import couponBg from "assets/images/aicopy/coupon-bg.png";
import couponBgActive from "assets/images/aicopy/coupon-bg-active.png";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { XtContext } = Context;

/** next/webpack 静态资源：兼容 string | { src } | { default } */
function resolveAssetUrl(asset) {
  if (asset == null) return "";
  if (typeof asset === "string") return asset;
  if (typeof asset === "object") {
    if (typeof asset.src === "string") return asset.src;
    if (typeof asset.default === "string") return asset.default;
    if (asset.default?.src) return asset.default.src;
  }
  return String(asset);
}

const COUPON_BG_URL = resolveAssetUrl(couponBg);
const COUPON_BG_ACTIVE_URL = resolveAssetUrl(couponBgActive);

/** 用券下单：输入框展示券面值（两位小数） */
function formatVoucherAmountForInput(amount) {
  if (amount == null || amount === "") return "";
  const n = typeof amount === "number" ? amount : parseFloat(String(amount).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(amount);
  return n.toFixed(2);
}

/** 券卡角标：与稿面「WWL***8D0」类似，优先可读字段再兜底 id */
function formatVoucherChipMaskCode(item) {
  if (!item) return "—";
  const raw = item.raw || {};
  const ref = String(
    raw.sourceRefId ||
      raw.source_ref_id ||
      raw.lockRef ||
      raw.lock_ref ||
      raw.idempotencyKey ||
      raw.idempotency_key ||
      item.id ||
      ""
  ).trim();
  const alnum = ref.replace(/[^a-zA-Z0-9]/g, "");
  if (alnum.length >= 6) {
    return `${alnum.slice(0, 3)}***${alnum.slice(-3)}`;
  }
  if (alnum.length > 0) return alnum;
  return item.code || "—";
}

function OrderForm({
  // 金额
  value,
  onChange,
  onClear,
  // 币种
  currencyOptions = ["USDT"],
  selectedCurrency,
  onCurrencyChange,
  showCurrencyDropdown,
  onCurrencyDropdownToggle,
  onCloseDropdown,
  // 门槛与余额（校验、滑块、Amount range 文案均用 maxInCurrent）
  minInCurrent,
  maxInCurrent,
  amountRangeLabelMax,
  formatNumber,
  availableBalanceText,
  amountRangeErrorMsg,
  // 滑块
  currentAvailableNum,
  currentAvailableBalanceStr,
  maxInCurrentForSlider,
  onSliderChange,
  // 操作
  onUseAll,
  onGoDeposit,
  onGoFiatDeposit,
  /** 用券下单时「+」跳转我的券（与充值入口区分） */
  onGoVoucher,
  /** 外链带参：优先选中该 userCouponId（须在可用券列表中） */
  preferredVoucherUserCouponId,
  /** 选中券变化（可选，供后续下单传 userCouponId） */
  onSelectedVoucherChange,
  // 提交按钮
  submitLabel,
  onSubmit,
  submitting,
  submitDisabled,
  // 可选：策略跟单卡片 — 预估每日收益文案（已含金额与币种，与 Available 一致）
  estimatedDailyProfitText,
  // 可选：卡片标题与说明点击
  cardTitle,
  onCardTitleClick,
}) {
  const t = useTranslation();
  const currencyWrapRef = useRef(null);
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const userId = appState?.userInfo?.userId;
  const tenantId = useMemo(() => resolveCouponTenantId(appState), [appState]);
  const [voucherItems, setVoucherItems] = useState([]);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherMc, setVoucherMc] = useState(null);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const onSelectedVoucherChangeRef = useRef(onSelectedVoucherChange);
  onSelectedVoucherChangeRef.current = onSelectedVoucherChange;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const rangeMaxForDisplay =
    amountRangeLabelMax != null && Number.isFinite(amountRangeLabelMax) ? amountRangeLabelMax : maxInCurrent;
  const voucherRangeMin = minInCurrent;
  const voucherRangeMax = rangeMaxForDisplay;
  const isVoucherSelectable = (item) =>
    isVoucherItemSelectableForFollow(item, voucherRangeMin, voucherRangeMax);

  useEffect(() => {
    if (!onCloseDropdown || !showCurrencyDropdown) return;
    const onDocClick = (e) => {
      if (currencyWrapRef.current?.contains(e.target)) return;
      onCloseDropdown();
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [showCurrencyDropdown, onCloseDropdown]);

  const loadVoucherItems = React.useCallback(
    (forceRefresh = false) => {
      if (!isAicopyVoucherOrderOption(selectedCurrency)) {
        setVoucherItems([]);
        setVoucherMc(null);
        setVoucherLoading(false);
        return Promise.resolve();
      }
      if (!loginStatus || userId == null || userId === "") {
        setVoucherItems([]);
        setVoucherMc(null);
        setVoucherLoading(false);
        return Promise.resolve();
      }
      setVoucherLoading(true);
      setVoucherMc(null);
      return fetchAicopyUsableVouchersCached({ tenantId, userId, forceRefresh })
        .then((res) => {
          if (!res.ok) {
            setVoucherItems([]);
            setVoucherMc(res.mc);
          } else {
            setVoucherItems(res.items || []);
          }
        })
        .finally(() => {
          setVoucherLoading(false);
        });
    },
    [selectedCurrency, loginStatus, userId, tenantId]
  );

  useEffect(() => {
    loadVoucherItems(false);
  }, [loadVoucherItems]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onInvalidate = () => {
      loadVoucherItems(true);
    };
    window.addEventListener(AICOPY_USABLE_VOUCHERS_INVALIDATE_EVENT, onInvalidate);
    return () => window.removeEventListener(AICOPY_USABLE_VOUCHERS_INVALIDATE_EVENT, onInvalidate);
  }, [loadVoucherItems]);

  useEffect(() => {
    if (!isAicopyVoucherOrderOption(selectedCurrency)) {
      setSelectedVoucherId(null);
      return;
    }
    if (!voucherItems.length) {
      setSelectedVoucherId(null);
      return;
    }
    setSelectedVoucherId((prev) => {
      const preferred =
        preferredVoucherUserCouponId != null && String(preferredVoucherUserCouponId).trim() !== ""
          ? String(preferredVoucherUserCouponId).trim()
          : null;
      if (preferred) {
        const hit = voucherItems.find((x) => String(x.id) === preferred);
        if (hit?.id != null && isVoucherItemSelectableForFollow(hit, voucherRangeMin, voucherRangeMax)) {
          return String(hit.id);
        }
      }
      if (
        prev &&
        voucherItems.some(
          (x) => String(x.id) === String(prev) && isVoucherItemSelectableForFollow(x, voucherRangeMin, voucherRangeMax)
        )
      ) {
        return prev;
      }
      const firstOk = voucherItems.find((x) => isVoucherItemSelectableForFollow(x, voucherRangeMin, voucherRangeMax));
      return firstOk?.id != null ? String(firstOk.id) : null;
    });
  }, [selectedCurrency, voucherItems, preferredVoucherUserCouponId, voucherRangeMin, voucherRangeMax]);

  useEffect(() => {
    const cb = onSelectedVoucherChangeRef.current;
    if (!cb) return;
    if (!isAicopyVoucherOrderOption(selectedCurrency)) {
      cb(null);
      return;
    }
    // 券列表加载中不向父组件上报 null，避免多策略卡片互相冲掉已选券
    if (voucherLoading) return;
    if (!selectedVoucherId) {
      cb(null);
      return;
    }
    const row = voucherItems.find((x) => String(x.id) === String(selectedVoucherId));
    cb(row || null);
  }, [selectedVoucherId, voucherItems, voucherLoading, selectedCurrency]);

  const isVoucherMode = isAicopyVoucherOrderOption(selectedCurrency);
  const selectedVoucherRow = useMemo(() => {
    if (!selectedVoucherId) return null;
    return voucherItems.find((x) => String(x.id) === String(selectedVoucherId)) || null;
  }, [selectedVoucherId, voucherItems]);

  /** 用券：仅展示已选券面值；未选中/加载中为空，避免闪旧金额 */
  const voucherInputValue = useMemo(() => {
    if (!isVoucherMode) return null;
    if (!selectedVoucherRow || selectedVoucherRow.amount == null || selectedVoucherRow.amount === "") {
      return "";
    }
    return formatVoucherAmountForInput(selectedVoucherRow.amount);
  }, [isVoucherMode, selectedVoucherRow]);

  const inputDisplayValue = voucherInputValue !== null ? voucherInputValue : value ?? "";

  useEffect(() => {
    if (!isVoucherMode) return;
    const cb = onChangeRef.current;
    if (!cb) return;
    const target = voucherInputValue ?? "";
    if (value !== target) cb(target);
  }, [isVoucherMode, voucherInputValue, value]);

  const selectedVoucherInRange = useMemo(() => {
    if (!selectedVoucherRow) return false;
    return isVoucherAmountInFollowRange(
      parseVoucherFaceAmount(selectedVoucherRow),
      voucherRangeMin,
      voucherRangeMax
    );
  }, [selectedVoucherRow, voucherRangeMin, voucherRangeMax]);

  const hardCap =
    maxInCurrent != null && Number.isFinite(maxInCurrent) && maxInCurrent > 0 ? maxInCurrent : Infinity;
  const hardCapForResolve = Number.isFinite(hardCap) && hardCap > 0 && hardCap !== Infinity ? hardCap : null;
  let sliderMaxStr = "0";
  if (parseAicopyBalanceNum(currentAvailableBalanceStr ?? "") > 0) {
    sliderMaxStr = resolveAicopySliderMaxBalanceStr(currentAvailableBalanceStr, hardCapForResolve);
  } else if (maxInCurrentForSlider > 0) {
    sliderMaxStr = resolveAicopySliderMaxBalanceStr(String(maxInCurrentForSlider), hardCapForResolve);
  } else if (hardCapForResolve) {
    sliderMaxStr = normalizeAicopyBalanceRaw(String(Math.min(10000, hardCapForResolve)));
  } else {
    sliderMaxStr = "10000";
  }

  return (
    <div className={cx(styles.orderFormCard, "aicopy-orderform-card")}>
      {(cardTitle != null || onCardTitleClick) && (
        <div className={styles.cardTitle} onClick={onCardTitleClick}>
          {cardTitle ?? t("aicopy.investmentAmount")}
        </div>
      )}

      {/* 输入区域：金额 + 清除 + 币种 */}
      <div className={styles.inputSection}>
        <div
          className={cx(
            styles.inputWrapper,
            amountRangeErrorMsg && !isVoucherMode && styles.inputWrapperError,
            isVoucherMode && styles.inputWrapperVoucherLocked
          )}
        >
          <input
            type="text"
            className={cx(styles.amountInput, isVoucherMode && styles.amountInputReadOnly)}
            value={inputDisplayValue}
            readOnly={isVoucherMode}
            aria-readonly={isVoucherMode}
            onChange={(e) => {
              if (isVoucherMode) return;
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d*$/.test(v)) onChange(v);
            }}
            placeholder="0.00"
          />
          {inputDisplayValue && !isVoucherMode ? (
            <div className={styles.clearButton} onClick={onClear}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ) : null}
          <span className={styles.currencyDivider} aria-hidden />
          <div
            ref={currencyWrapRef}
            className={styles.currencySelectWrap}
            onClick={(e) => currencyOptions.length > 1 && e.stopPropagation()}
          >
            <div
              className={styles.currencySelect}
              onClick={() =>
                currencyOptions.length > 1 && onCurrencyDropdownToggle?.()
              }
            >
              <span className={styles.currencySelectText}>
                {isAicopyVoucherOrderOption(selectedCurrency) ? t("aicopy.orderWithVoucher") : selectedCurrency}
              </span>
              {currencyOptions.length > 1 && (
                <span className={styles.currencySelectArrow}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none" aria-hidden>
                    <path d="M0 0L0 1.5L3 4.68L6 1.5L6 0Z" fill="#929AA5" />
                  </svg>
                </span>
              )}
            </div>
            {showCurrencyDropdown && currencyOptions.length > 1 && (
              <div className={styles.currencyDropdown}>
                {currencyOptions.map((opt) => (
                  <div
                    key={opt}
                    className={`${styles.currencyOption} ${selectedCurrency === opt ? styles.currencyOptionActive : ""}`}
                    onClick={() => {
                      const toVoucher = isAicopyVoucherOrderOption(opt);
                      const fromVoucher = isAicopyVoucherOrderOption(selectedCurrency);
                      if (toVoucher !== fromVoucher) {
                        onChangeRef.current?.("");
                      }
                      onCurrencyChange?.(opt);
                      onCloseDropdown?.();
                    }}
                  >
                    {isAicopyVoucherOrderOption(opt) ? (
                      <span className={styles.currencyOptionInner}>
                        <span className={styles.voucherOptionIcon} aria-hidden>
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M10 0C15.5234 0 20 4.47656 20 10C20 15.5234 15.5234 20 10 20C4.47656 20 0 15.5234 0 10C0 4.47656 4.47656 0 10 0Z"
                              fill="#B9F78A"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.49859 6.03642C5.26245 6.11589 5.04134 6.3182 4.94805 6.54015L4.89355 6.6698V7.98852V9.30724L4.96778 9.45782C5.07772 9.68085 5.18293 9.77946 5.44186 9.90219C5.7816 10.0632 5.92153 10.2601 5.89196 10.5356C5.86543 10.7827 5.75205 10.9205 5.4637 11.0562C5.19927 11.1807 5.03491 11.3354 4.94845 11.5412C4.8945 11.6696 4.89355 11.7884 4.89355 12.6038C4.89355 13.4207 4.89443 13.4358 4.94879 13.5653C5.02133 13.738 5.21578 13.9391 5.39434 14.026L5.5322 14.0931L6.64391 14.1004C7.41411 14.1054 7.7937 14.0997 7.87956 14.0817C8.0646 14.0431 8.26655 13.9029 8.38066 13.7339C8.53963 13.4985 8.55068 13.4869 8.66078 13.4401C8.86113 13.3549 9.06242 13.4365 9.17406 13.6483C9.2521 13.7963 9.41878 13.9514 9.5831 14.029L9.71885 14.0931H12.4153H15.2549L15.3928 14.026C15.5713 13.9391 15.7658 13.738 15.8383 13.5653L15.8936 13.4337V10.0007V6.6698L15.8383 6.53826C15.7658 6.36549 15.5713 6.16441 15.3928 6.07751L15.2549 6.01043L12.4522 6.00436C9.46876 5.99757 9.68439 5.9859 9.42914 6.16793C9.3543 6.22129 9.26648 6.30803 9.23395 6.36071C9.10173 6.57485 9.07557 6.60377 8.96755 6.65529C8.74513 6.76136 8.57426 6.67982 8.38243 6.37613C8.28964 6.22923 8.13357 6.10706 7.95568 6.04208C7.846 6.00202 7.76101 5.99913 6.72052 6.00016C5.7749 6.00107 5.58707 6.00664 5.49859 6.03642ZM5.71487 6.76204L5.66229 6.79742L5.66264 7.97276C5.66286 8.61919 5.67083 9.15852 5.68039 9.17128C5.68994 9.18405 5.78269 9.23435 5.88648 9.28309C6.14593 9.40492 6.4021 9.64995 6.52339 9.89239C6.75108 10.3474 6.71326 10.835 6.41785 11.2531C6.29175 11.4316 6.11277 11.5763 5.87517 11.6917L5.67412 11.7893L5.66761 12.5944C5.6612 13.2884 5.66175 13.2978 5.71163 13.3371C5.75787 13.3735 5.84215 13.3769 6.70723 13.3769C7.31387 13.3769 7.66869 13.3685 7.69807 13.3533C7.72326 13.3404 7.7695 13.2816 7.80084 13.2226C7.93153 12.9769 8.18731 12.7778 8.48345 12.6914C8.69544 12.6295 9.01322 12.6403 9.21796 12.7163C9.41826 12.7908 9.62769 12.9533 9.747 13.1268C9.80289 13.2081 9.86514 13.2976 9.88537 13.3257L9.9221 13.3769H12.3993C14.6032 13.3769 15.0255 13.373 15.0722 13.3415L15.1248 13.3061V10.0007V6.79742L15.0722 6.76204C15.0255 6.73055 14.604 6.72668 12.4041 6.72698C11.0443 6.72718 9.91986 6.73484 9.90533 6.74403C9.89083 6.75322 9.85701 6.80237 9.83016 6.85326C9.42356 7.62439 8.2666 7.66006 7.82412 6.91512C7.70107 6.70795 7.80978 6.72664 6.7283 6.72664C5.89691 6.72664 5.76033 6.73141 5.71487 6.76204ZM11.7079 8.44207C11.657 8.45989 11.5965 8.49304 11.5735 8.51571C11.5504 8.53838 11.0676 9.11455 10.5006 9.79608C9.74863 10.6999 9.4628 11.0596 9.44419 11.1253C9.35298 11.4473 9.7878 11.7204 10.0512 11.5066C10.1038 11.464 12.0629 9.11714 12.1597 8.98079C12.2097 8.91049 12.2171 8.8782 12.209 8.76695C12.1984 8.62239 12.147 8.53599 12.0344 8.4734C11.9327 8.41683 11.8127 8.40532 11.7079 8.44207ZM9.5989 8.5886C9.42436 8.66479 9.30536 8.85042 9.30506 9.047C9.30487 9.15616 9.40766 9.3473 9.50488 9.41858C9.84012 9.66436 10.322 9.43429 10.322 9.02844C10.322 8.67575 9.93646 8.44123 9.5989 8.5886ZM11.6348 10.5206C11.4143 10.6171 11.2781 10.8741 11.3366 11.083C11.452 11.4951 12.0078 11.5939 12.2527 11.2459C12.2934 11.1881 12.3335 11.0966 12.342 11.0426C12.3995 10.6726 11.9856 10.367 11.6348 10.5206Z"
                              fill="black"
                              stroke="black"
                              strokeWidth="0.3"
                            />
                          </svg>
                        </span>
                        <span>{t("aicopy.orderWithVoucher")}</span>
                      </span>
                    ) : (
                      <>
                        <FiatCurrencyIcon code={opt} className={styles.currencyOptionIcon} style={{ background: "#26A69A" }} />
                        <span className={styles.currencyOptionLabel}>{opt}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {amountRangeErrorMsg && !isVoucherMode && (
          <div className={styles.amountErrorTip}>{amountRangeErrorMsg}</div>
        )}
        {isAicopyVoucherOrderOption(selectedCurrency) ? (
          <div className={styles.voucherStrip}>
            {voucherLoading ? (
              <div className={styles.voucherStripHint}>{t("aicopy.voucherStripLoading")}</div>
            ) : voucherMc ? (
              <div className={styles.voucherStripHint}>
                {translateVoucherApiMc(t, voucherMc, "activity.voucherLoadFailed")}
              </div>
            ) : voucherItems.length === 0 ? (
              <div className={styles.voucherStripHint}>{t("aicopy.voucherStripEmpty")}</div>
            ) : (
              <div className={styles.voucherStripScroll}>
                {voucherItems.map((item, idx) => {
                  const idKey = item.id != null ? String(item.id) : `v-${idx}`;
                  const disabled = !isVoucherSelectable(item);
                  const selected =
                    !disabled && selectedVoucherId != null && String(selectedVoucherId) === String(item.id);
                  const selectVoucher = (e) => {
                    e?.stopPropagation?.();
                    if (disabled || item.id == null) return;
                    const nextId = String(item.id);
                    if (String(selectedVoucherId) === nextId) return;
                    onChangeRef.current?.("");
                    setSelectedVoucherId(nextId);
                  };
                  const ariaLabel = [item.amount, item.unit, item.displayDescription || item.name || item.couponName]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <div
                      key={idKey}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-pressed={selected}
                      aria-disabled={disabled}
                      aria-label={ariaLabel || t("aicopy.orderWithVoucher")}
                      className={cx(styles.voucherTicket, disabled && styles.voucherTicketDisabled)}
                      style={{
                        backgroundImage: `url(${selected ? COUPON_BG_ACTIVE_URL : COUPON_BG_URL})`,
                      }}
                      onClick={selectVoucher}
                      onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          selectVoucher(e);
                        }
                      }}
                    >
                      <div className={styles.voucherTicketBody}>
                        <div className={styles.voucherTicketAmount}>
                          <span className={styles.voucherTicketAmountNum}>{item.amount}</span>
                          {item.unit ? <span className={styles.voucherTicketUnit}>{item.unit}</span> : null}
                        </div>
                        <div className={styles.voucherTicketDesc}>
                          {item.displayDescription || item.name || item.couponName || "—"}
                        </div>
                        <div className={styles.voucherTicketCode}>{formatVoucherChipMaskCode(item)}</div>
                      </div>
                      <div className={styles.voucherTicketExpiry}>
                        {t("activity.voucherExpiresOn", {
                          time: item.expireTime || item.expiryDate || "—",
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
        <div className={cx(styles.amountInfo, isVoucherMode && styles.amountInfoVoucher)}>
          <div className={styles.amountInfoLeft}>
            {minInCurrent != null &&
              rangeMaxForDisplay != null &&
              Number.isFinite(minInCurrent) &&
              Number.isFinite(rangeMaxForDisplay) && (
                <span className={styles.amountRange}>
                  {t("aicopy.investmentTotalAmount", {
                    min: formatAicopyAmountRangeLabel(minInCurrent, formatNumber),
                    max: formatAicopyAmountRangeLabel(rangeMaxForDisplay, formatNumber),
                  })}
                </span>
              )}
            {!isVoucherMode && estimatedDailyProfitText ? (
              <div className={styles.estimatedDailyProfit}>{estimatedDailyProfitText}</div>
            ) : null}
          </div>
          <div className={styles.amountInfoRight}>
            {isVoucherMode ? (
              estimatedDailyProfitText ? (
                <div className={styles.estimatedDailyProfit}>{estimatedDailyProfitText}</div>
              ) : null
            ) : (
            <div className={styles.availableBalance} onClick={onUseAll}>
              <span className={styles.availableLabel}>
                {t("aicopy.available")}
              </span>
              <span className={styles.availableValue}>{availableBalanceText}</span>
              <div
                className={styles.addIcon}
                onClick={(e) => {
                  e?.stopPropagation?.();
                  if (selectedCurrency === "USDT" || selectedCurrency === "USDC") {
                    // USDT / USDC 均走现货充值；USDC 支付前会先兑换为 USDT
                    onGoDeposit?.(e);
                  } else {
                    onGoFiatDeposit?.(e);
                  }
                }}
                role="button"
                aria-label="Deposit"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle
                    cx="10"
                    cy="10"
                    r="9"
                    fill="#b9f78a"
                    stroke="none"
                  />
                  <path
                    d="M10 6V14M6 10H14"
                    stroke="#181a20"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* 滑块：用券下单不展示 */}
      {!isVoucherMode ? (
        <div className={styles.sliderSection}>
          <TradeSlider
            className={styles.slider}
            value={inputDisplayValue}
            max={sliderMaxStr}
            point={2}
            onChange={onSliderChange}
            noBtnAll={true}
          />
        </div>
      ) : null}

      {/* 提交按钮：父组件可传入 submitLabel 区分「加仓」/「立即跟单」 */}
      {submitLabel != null && (
        <div className={styles.footer}>
          <button
            type="button"
            className={cx(
              styles.confirmBtn,
              submitDisabled && !submitting && styles.confirmBtnMuted
            )}
            onClick={onSubmit}
            disabled={submitting}
          >
            {submitting ? t("aicopy.submitting") : submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}

export default OrderForm;
